import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Empty, Input, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import {
  CloudOutlined,
  CloudServerOutlined,
  CloudUploadOutlined,
  DownloadOutlined,
  FolderOpenOutlined,
  ReloadOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { Button, Card } from "../../../components/Common";
import { useMutations } from "../../../hooks/useApiMutation";
import axiosClient from "../../../api/axiosClient";
import { handleAxiosError } from "../../../components/Common/apiHelpers";
import RemoteRestoreModal from "./RemoteRestoreModal";
import {
  BACKUPS_ENDPOINT,
  BACKUP_PROVIDER,
  BACKUP_PROVIDER_OPTIONS,
  FTP_ENDPOINT,
  formatBytes,
} from "../constants";

const { Text } = Typography;

/**
 * The archives that are actually sitting on the off-site target right now, read straight off
 * FTP or out of the bucket rather than out of the local catalogue. That is the point of the
 * screen: after the server is rebuilt the catalogue is empty, but the archives are still there.
 */
const BackupFilesPanel = ({ onRestored }) => {
  const { mutate } = useMutations();
  // Stable handle for the load effects — see the note in BackupsPage.
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  const [provider, setProvider] = useState(BACKUP_PROVIDER.FTP);
  const [ftpConnectionId, setFtpConnectionId] = useState(null);
  const [search, setSearch] = useState("");

  const [connections, setConnections] = useState([]);
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [restoring, setRestoring] = useState(null);

  const load = useCallback(
    async (term) => {
      setLoading(true);
      setFailure(null);

      await mutateRef.current("listRemoteBackupFiles", {
        method: "get",
        endpoint: `${BACKUPS_ENDPOINT}/files`,
        params: {
          provider,
          // Null lets the server pick the target the schedule uploads to.
          ftpConnectionId: provider === BACKUP_PROVIDER.FTP ? ftpConnectionId ?? undefined : undefined,
          search: term || undefined,
          take: 500,
        },
        onSuccess: (data) => setListing(data || null),
        // An unreachable target is an ordinary state on this screen, so it is explained in
        // place rather than only in a toast that scrolls away.
        onError: (error) => {
          setListing(null);
          setFailure(
            error?.response?.data?.message || error?.message || "The target could not be read."
          );
        },
      });

      setLoading(false);
    },
    [provider, ftpConnectionId]
  );

  useEffect(() => {
    mutateRef.current("backupFilesFtpConnections", {
      method: "get",
      endpoint: FTP_ENDPOINT,
      onSuccess: (data) => setConnections((data || []).filter((c) => c.isActive)),
    });
  }, []);

  // Typing in the search box re-reads the target, which keeps the list honest for a directory
  // too large to send in one go; the delay stops a keystroke from opening an FTP session.
  useEffect(() => {
    const timer = setTimeout(() => load(search), 350);
    return () => clearTimeout(timer);
  }, [load, search]);

  // The archive is a file download rather than a JSON payload, so it bypasses the shared
  // mutate helper and goes straight through the axios client.
  const download = async (record) => {
    setDownloading(record.path);

    try {
      const response = await axiosClient.get(`${BACKUPS_ENDPOINT}/files/download`, {
        params: {
          provider,
          ftpConnectionId: provider === BACKUP_PROVIDER.FTP ? ftpConnectionId ?? undefined : undefined,
          path: record.path,
        },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = record.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      handleAxiosError(error, "Could not download that file.");
    } finally {
      setDownloading(null);
    }
  };

  const columns = [
    {
      title: "File",
      dataIndex: "fileName",
      key: "fileName",
      // Same reasoning as the archive list: the name identifies the row, so it keeps one
      // line and the remote path underneath is what wraps.
      width: 360,
      render: (fileName, record) => (
        <Space direction="vertical" size={0}>
          <Space size={8}>
            <Text strong style={{ whiteSpace: "nowrap" }}>
              {fileName}
            </Text>
            {!record.isArchive && <Tag>not an archive</Tag>}
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.path}
          </Text>
        </Space>
      ),
    },
    {
      title: "Size",
      dataIndex: "sizeBytes",
      key: "sizeBytes",
      width: 110,
      align: "right",
      render: (bytes) => (bytes ? formatBytes(bytes) : <Text type="secondary">—</Text>),
    },
    {
      title: "Last written",
      dataIndex: "modifiedAt",
      key: "modifiedAt",
      width: 190,
      render: (date) =>
        date ? (
          <Text style={{ whiteSpace: "nowrap" }}>{new Date(date).toLocaleString()}</Text>
        ) : (
          <Text type="secondary">unknown</Text>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <Space size={0}>
          <Tooltip
            title={
              record.isArchive
                ? "Fetch it back and restore it"
                : "Only a .kbak archive can be restored"
            }
          >
            <Button
              variant="ghost"
              icon={<UndoOutlined />}
              disabled={!record.isArchive}
              onClick={() => setRestoring(record)}
            />
          </Tooltip>

          <Tooltip title="Download">
            <Button
              variant="ghost"
              icon={<DownloadOutlined />}
              loading={downloading === record.path}
              onClick={() => download(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message="What is on the off-site target"
        description="Read live from the FTP target or the bucket with the stored credentials, so archives this server has no record of — one uploaded before it was rebuilt — are listed here too. Downloading or restoring leaves the remote copy exactly where it is."
      />

      <Card
        title={
          <Space size={8}>
            <FolderOpenOutlined />
            Backup files
          </Space>
        }
        extra={
          <Button variant="secondary" icon={<ReloadOutlined />} onClick={() => load(search)}>
            Refresh
          </Button>
        }
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Space size={12} wrap>
            <Select
              value={provider}
              onChange={(value) => {
                setProvider(value);
                setListing(null);
              }}
              options={BACKUP_PROVIDER_OPTIONS}
              style={{ width: 180 }}
              prefix={provider === BACKUP_PROVIDER.S3 ? <CloudOutlined /> : <CloudUploadOutlined />}
            />

            {provider === BACKUP_PROVIDER.FTP && (
              <Select
                allowClear
                value={ftpConnectionId}
                onChange={(value) => setFtpConnectionId(value ?? null)}
                placeholder="Same target as the automatic backup"
                style={{ width: 340 }}
                options={connections.map((c) => ({
                  value: c.ftpConnectionId,
                  label: `${c.name} — ${c.host}${c.remoteDirectory ? c.remoteDirectory : ""}`,
                }))}
              />
            )}

            <Input
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by file name"
              style={{ width: 260 }}
            />

            {listing?.location && (
              <Space size={6}>
                <CloudServerOutlined />
                <Text type="secondary">
                  {listing.location}
                  {listing.ftpConnectionName ? ` · ${listing.ftpConnectionName}` : ""}
                </Text>
              </Space>
            )}
          </Space>

          {failure && <Alert type="error" showIcon message={failure} />}

          <Table
            rowKey="path"
            size="small"
            loading={loading}
            columns={columns}
            dataSource={listing?.files || []}
            pagination={{ pageSize: 15, size: "small", showSizeChanger: false }}
            scroll={{ x: 780 }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    search
                      ? "No file on the target matches that name."
                      : "There is nothing on this target yet."
                  }
                />
              ),
            }}
          />
        </Space>
      </Card>

      <RemoteRestoreModal
        file={restoring}
        provider={provider}
        ftpConnectionId={provider === BACKUP_PROVIDER.FTP ? ftpConnectionId : null}
        open={restoring !== null}
        onClose={() => setRestoring(null)}
        onDone={onRestored}
      />
    </Space>
  );
};

export default BackupFilesPanel;
